import type {
  GrantDraftExportFormat,
  GrantOpportunityDocumentLink,
  GrantOpportunityDocumentRole,
} from "@/api/grant_opportunity_documents";

export type GrantArtifactType = "budget" | "progress_report" | "use_of_funds";

// Artifact types whose drafts carry line items that can export as CSV.
export const GRANT_ARTIFACT_TABULAR_TYPES: GrantArtifactType[] = ["budget", "use_of_funds"];

export const GRANT_ARTIFACT_TYPE_LABELS: Record<GrantArtifactType, string> = {
  budget: "Budget",
  progress_report: "Progress report",
  use_of_funds: "Use-of-funds plan",
};

export const GRANT_ARTIFACT_ROLE_TO_TYPE: Partial<
  Record<GrantOpportunityDocumentRole, GrantArtifactType>
> = {
  budget_draft: "budget",
  progress_report_draft: "progress_report",
  use_of_funds_draft: "use_of_funds",
};

export type GrantArtifactPayload = {
  markdown: string;
  label?: string;
};

export type GrantArtifactWithMarkdown = GrantOpportunityDocumentLink & {
  markdown: string;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function artifactsPath(municipalityId: number, opportunityId: number, linkId?: number): string {
  const base = `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/artifacts`;
  return linkId ? `${base}/${linkId}` : base;
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}

export type GrantArtifactGenerationStatus = "pending" | "processing" | "completed" | "failed";

export type GrantArtifactGeneration = {
  id: number;
  grant_opportunity_id: number;
  artifact_type: GrantArtifactType;
  status: GrantArtifactGenerationStatus;
  current_stage: string | null;
  error_message: string | null;
  started_at: string | null;
  duration_ms: number | null;
  created_at: string;
  markdown?: string;
};

function generationsPath(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/artifact_generations`;
}

export async function createGrantArtifactGeneration(
  municipalityId: number,
  opportunityId: number,
  artifactType: GrantArtifactType,
): Promise<GrantArtifactGeneration> {
  const response = await fetch(generationsPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ artifact_type: artifactType }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Could not generate artifact draft");
  }
  return data;
}

export async function getGrantArtifactGeneration(
  municipalityId: number,
  opportunityId: number,
  generationId: number,
): Promise<GrantArtifactGeneration> {
  const response = await fetch(`${generationsPath(municipalityId, opportunityId)}/${generationId}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error ?? "Could not load artifact draft status");
  }
  return data;
}

export async function saveGrantArtifact(
  municipalityId: number,
  opportunityId: number,
  artifactType: GrantArtifactType,
  payload: GrantArtifactPayload,
): Promise<GrantOpportunityDocumentLink> {
  const response = await fetch(artifactsPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      artifact: {
        artifact_type: artifactType,
        markdown: payload.markdown,
        label: payload.label ?? null,
      },
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Failed to save artifact draft");
  }
  return data;
}

export async function fetchGrantArtifact(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
): Promise<GrantArtifactWithMarkdown> {
  const response = await fetch(artifactsPath(municipalityId, opportunityId, linkId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Failed to load artifact draft");
  }
  return data;
}

export async function updateGrantArtifact(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
  payload: GrantArtifactPayload,
): Promise<GrantOpportunityDocumentLink> {
  const response = await fetch(artifactsPath(municipalityId, opportunityId, linkId), {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      artifact: {
        markdown: payload.markdown,
        label: payload.label ?? null,
      },
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Failed to update artifact draft");
  }
  return data;
}

export async function downloadGrantArtifact(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
  format: GrantDraftExportFormat = "docx",
): Promise<void> {
  const response = await fetch(
    `${artifactsPath(municipalityId, opportunityId, linkId)}/export?format=${format}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw new Error("Failed to export artifact draft");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
    `grant-artifact-${linkId}.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
